import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import AttachmentRow from '@src/components/AttachmentRow'
import { absoluteMediaUrl } from '@src/services/media'
import { AgreementStatusChip } from './MyMemberStatusChip'
import { agreementStatusOf, endedAgreementNote } from './agreementStatus'

function formatTimestamp(iso) {
  return iso ? moment(iso).format('D MMM YYYY, HH:mm') : null
}

function Row({ label, children }) {
  const empty = children === null || children === undefined || children === ''
  return (
    <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-3 last:border-b-0">
      <span className="shrink-0 text-sm text-gray-500">{label}</span>
      <span className="min-w-0 break-words text-right text-sm font-medium text-gray-900">
        {empty ? '—' : children}
      </span>
    </div>
  )
}

Row.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node }
Row.defaultProps = { children: null }

/**
 * "Membership agreement" card: where the DocuSign agreement stands (client
 * signature → CLIK review → CLIK countersignature) and the signed PDF.
 *
 * Reads the `agreement` block of the company detail payloads (falls back to the
 * flat agreement_status / client_signed_at / clik_signed_at fields). Renders
 * nothing when no agreement was sent yet. The PDF is opened through the
 * caller's FileViewer (`onView`), which fetches it with the Bearer token — the
 * uploads proxy is auth-gated, so a plain link would 401.
 *
 * A declined / voided agreement is shown as it is (it stays the latest one
 * until a new envelope is sent), with a note on what happens next: sign it
 * again (the company is back at the agreement step), or wait for Everest/CLIK
 * to reset it.
 */
export default function AgreementCard({ company, onView, showEndedNote }) {
  const status = agreementStatusOf(company)
  const agreement = company?.agreement || {}
  if (!status) return null

  const sequential = agreement.signing_flow !== 'single'
  const clientSignedAt = agreement.client_signed_at ?? company?.client_signed_at
  const clikSignedAt = agreement.clik_signed_at ?? company?.clik_signed_at
  const documentPath = agreement.signed_document_url

  // Before CLIK countersigns, the stored PDF carries the client's signature
  // only; the backend replaces it with the executed one once CLIK has signed.
  const fullySigned = status === 'executed'
  // Declined / voided: what happens next (sign again, or wait for the reset).
  const endedNote = showEndedNote ? endedAgreementNote(company) : null

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
        <h3 className="text-sm font-semibold text-gray-900">Membership agreement</h3>
        <AgreementStatusChip status={status} />
      </div>
      {sequential ? (
        <>
          <Row label="Client signed">{formatTimestamp(clientSignedAt)}</Row>
          <Row label="Sent to CLIK">{formatTimestamp(agreement.resumed_at)}</Row>
          <Row label="CLIK countersigned">{formatTimestamp(clikSignedAt)}</Row>
        </>
      ) : (
        <Row label="Signed">{formatTimestamp(agreement.executed_at ?? clientSignedAt)}</Row>
      )}
      {status === 'declined' ? (
        <>
          <Row label="Declined">{formatTimestamp(agreement.declined_at)}</Row>
          {agreement.decline_reason ? <Row label="Reason">{agreement.decline_reason}</Row> : null}
        </>
      ) : null}
      {status === 'voided' ? (
        <>
          <Row label="Voided">{formatTimestamp(agreement.voided_at)}</Row>
          {agreement.voided_reason ? <Row label="Reason">{agreement.voided_reason}</Row> : null}
        </>
      ) : null}
      {documentPath ? (
        <div className="p-2">
          <AttachmentRow
            name="Membership agreement.pdf"
            mimeType="application/pdf"
            meta={fullySigned ? 'Fully signed' : 'Client-signed'}
            onClick={() =>
              onView?.({
                url: absoluteMediaUrl(documentPath),
                fileName: 'Membership agreement.pdf',
                mimeType: 'application/pdf',
              })
            }
            last
          />
          {!fullySigned && sequential ? (
            <p className="px-4 pb-2 text-xs text-gray-500">
              This copy carries your signature only. It is replaced with the fully signed agreement
              once CLIK countersigns.
            </p>
          ) : null}
        </div>
      ) : null}
      {endedNote ? (
        <p role="note" className="border-t border-gray-200 px-5 py-3 text-xs text-gray-600">
          {endedNote}
        </p>
      ) : null}
    </div>
  )
}

AgreementCard.propTypes = {
  company: PropTypes.object,
  onView: PropTypes.func,
  // Off where the CLIK credential form next to the card already carries the
  // same note (its gate note).
  showEndedNote: PropTypes.bool,
}

AgreementCard.defaultProps = {
  company: null,
  onView: undefined,
  showEndedNote: true,
}
